import { getTopicForChecklist, ITopicList } from "@/api";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Stack,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChecklistTopicSections } from "./ChecklistTopicSections";

interface ChecklistTopicAccordionProps {
    checklistId: number;
    topic: ITopicList;
    expanded: boolean;
    onToggle: () => void;
}

/**
 * Displays a single topic as an accordion.
 * Lazy loads topic details (clauses by section) when expanded.
 * Styled to match ScopeTopicsV2 TopicAccordion pattern.
 */
export const ChecklistTopicAccordion = ({
    checklistId,
    topic,
    expanded,
    onToggle,
}: ChecklistTopicAccordionProps) => {
    // Lazy load topic details only when expanded
    const {
        data: topicDetail,
        isPending,
        error,
    } = useQuery({
        queryKey: ["frontend-topic-detail", checklistId, topic.id],
        queryFn: () => getTopicForChecklist(checklistId, topic.id),
        enabled: expanded,
    });

    return (
        <Accordion
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={onToggle}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon color="primary" />}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 90,
                }}
            >
                <Stack gap={1}>
                    <Typography
                        color="primary"
                        fontWeight="bold"
                        variant="body2"
                        gutterBottom
                    >
                        {topic.name}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: "background.paper", p: 0 }}>
                <Stack gap={2} p={2}>
                    {isPending && <LoadingIndicator />}
                    {error && (
                        <Alert severity="error" variant="outlined">
                            Failed to load topic details. Please try again.
                        </Alert>
                    )}
                    {topicDetail && (
                        <ChecklistTopicSections
                            sections={topicDetail.clauses_by_section}
                        />
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};
